import type { CashReport, CashSession } from "./pages/KassaPage";
const BASE =(import.meta.env.VITE_API_BASE_URL || "").replace(/\/+$/, "") + "/api/v1";

export type FinanceStatus = {
  allocation_available: boolean; allocation_history_available: boolean;
  modules: { module: string; eligible: boolean; enabled: boolean; active: boolean; ready: boolean }[];
  intervals: { id: number; module: string; enabled_at: string; disabled_at: string | null; activation_date: string | null }[];
};
export type PayrollAgreement = { id: number; employee_id: number; effective_date: string; model: string; fixed: string; percent: string; reason: string };
export type PayrollEntry = { id: number; employee_id: number; branch_id: number | null; kind: string; recognition_date: string; amount: string; cost: string; reason: string; calculation: string; basis_id: number | null; service_id: number | null; event_id: number | null; allocation_id: number | null; agreement_id: number | null; reverses_id: number | null };
export type PayrollReport = {
  owed: string; employees: { employee_id: number; name: string; model: string | null; earned: string; paid: string; opening: string; balance: string; estimated: string }[];
  missing_agreements: { work_id: number; employee_id: number }[];
  unconfirmed_work_dates: { work_id: number; reason: string }[];
  entries: PayrollEntry[]; agreements: PayrollAgreement[];
  bases: { id: number; work_id: number; work_date: string; snapshot: string }[];
  share_revisions: { id: number; basis_id: number; service_id: number; shares: string; reason: string }[];
};
export type ExpenseRevision = { id: number; recognition_date: string; category: string; amount: string; note: string; reason: string };
export type ExpenseRow = { id: number; branch_id: number | null; employee_id: number | null; period_start: string | null; period_end: string | null; revision: ExpenseRevision; history: ExpenseRevision[]; payments: { id: number; payment_date: string; amount: string; method: string; reason: string }[]; paid: string; unpaid: string; status: string };
export type ExpenseReport = { expenses: ExpenseRow[]; recognized: string; paid: string; unpaid: string; partially_paid: string };

let token: string | null = localStorage.getItem("an_dashboard_token");

export function setToken(t: string | null) {
  token = t;
  if (t) localStorage.setItem("an_dashboard_token", t);
  else localStorage.removeItem("an_dashboard_token");
}

export function getToken() {
  return token;
}

export type Role = "platform_owner" | "workshop_owner" | "employee" | "client";

export type UserOut = {
  id: number;
  full_name: string;
  phone: string;
  role: Role;
  workshop_id: number | null;
  branch_id: number | null;
  can_be_usta?: boolean;
  can_be_sotuvchi?: boolean;
  workshop_expired?: boolean;
  must_change_password?: boolean;
};

export type TokenOut = { access_token: string; user: UserOut };

export type ApiError = {
  code: string;
  message: string;
  status: number;
  /**
   * Server `error` obyektidagi QO'SHIMCHA maydonlar. Backend `details` ni
   * konvertga yassilaydi (`main.py` app_error_handler), masalan shtrix-kod
   * nizosida `product_id` / `product_nomi` / `faol`. Ilgari bu yerda faqat
   * `code`/`message` olinardi va qolgani jimgina yo'qolardi.
   */
  details?: Record<string, unknown>;
};

// Ustaxona arxivlangani — sahifa qayta yuklanganda Login o'rniga blok ekrani
// ko'rsatish uchun belgi (App.tsx o'qiydi).
export const ARCHIVED_FLAG = "an_dashboard_workshop_archived";

// Obuna muddati tugagani (402 `expired`) — TariffProvider shu yerga ulanadi va
// tarif holatini qayta o'qiydi, shunda "muddat tugagan" banneri keyingi mount'ni
// kutmasdan, aynan rad etilgan amaldan keyin darhol chiqadi. Arxivdagi kabi
// reload QILINMAYDI: foydalanuvchi hozirgina to'ldirgan formani yo'qotmasligi
// kerak, xatoni esa har bir call site o'zi (toast/inline) ko'rsatadi.
let expiredHandler: (() => void) | null = null;

export function setExpiredHandler(fn: (() => void) | null): void {
  expiredHandler = fn;
}

function handleExpired(): void {
  expiredHandler?.();
}

/** Arxivlangan 403 ni markazlashtirilgan ishlash: token tozalanadi + reload. */
function handleArchived(): never {
  setToken(null);
  sessionStorage.setItem(ARCHIVED_FLAG, "1");
  window.location.reload();
  throw { code: "workshop_archived", message: "Ustaxona arxivlangan", status: 403 } as ApiError;
}

/**
 * Muvaffaqiyatsiz javobni `ApiError` ga aylantiradi — `req` va `downloadFile`
 * uchun YAGONA joy (ilgari ikkalasida ayni blok takrorlanardi).
 *
 * `code`/`message` doim ustun turadi; `error` obyektidagi qolgan hamma maydon
 * `details` ga tushadi, shunda server yuborgan kontekst (masalan shtrix-kod
 * nizosidagi tovar) UI gacha yetib boradi.
 *
 * Arxiv/muddat holatlari shu yerda ushlanadi: ikkalasi ham javob tanasini
 * o'qishni talab qiladi, va `handleArchived` umuman qaytmaydi.
 */
async function toApiError(r: Response): Promise<ApiError> {
  let body: { error?: Record<string, unknown> } = {};
  try { body = await r.json(); } catch { /* empty */ }
  const { code, message, ...details } = body.error ?? {};
  if (r.status === 403 && code === "workshop_archived") handleArchived();
  if (r.status === 402 && code === "expired") handleExpired();
  return {
    code: (code as string) || `http_${r.status}`,
    message: (message as string) || r.statusText,
    status: r.status,
    details,
  };
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((init?.headers as Record<string, string>) ?? {}),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const r = await fetch(`${BASE}${path}`, { ...init, headers });
  // Session-expiry handling: a 401 on an authenticated request means the token is
  // stale, so clear it and reload back to login. The login request itself is exempt —
  // its 401 (invalid_credentials) must surface as an inline error, not reload the page.
  if (r.status === 401 && !path.startsWith("/auth/login")) {
    setToken(null);
    window.location.reload();
    throw { code: "unauthorized", message: "Sessiya tugagan", status: 401 } as ApiError;
  }
  if (!r.ok) throw await toApiError(r);
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

/** Faylni (xlsx/pdf) auth bilan yuklab, brauzerda saqlashni boshlaydi. */
export async function downloadFile(path: string, filename: string): Promise<void> {
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const r = await fetch(`${BASE}${path}`, { headers });
  if (r.status === 401) {
    setToken(null);
    window.location.reload();
    throw { code: "unauthorized", message: "Sessiya tugagan", status: 401 } as ApiError;
  }
  if (!r.ok) throw await toApiError(r);
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export type Kpi = {
  revenue_today: string; // to'liq aylanma = labor + parts_in_works + standalone sales
  revenue_week: string;
  labor_today: string;
  parts_today: string; // parts_in_works + standalone sales
  works_today: number;
  works_week: number;
  new_clients_month: number;
  reminders_pending_week: number;
  reminders_failed_week: number;
  jami_debitorlik: string;
  tovar_sotuvi_bugun: string;
  with_telegram: number;
  total_clients: number;
  telegram_pct: number;
};

export type PaymentStatus = "tolandi" | "qisman" | "tolanmagan";

/**
 * Pul QANDAY qo'ldan-qo'lga o'tgani — to'lov holati emas, USULI.
 * `null` = noma'lum: mobil yozuvlar va bu imkoniyatdan oldingi barcha
 * hujjatlar. Ular hech qachon `naqd` deb to'ldirilmaydi.
 */
export type PaymentMethod = "naqd" | "terminal" | "karta_otkazma" | "bank";

export type WorkPayment = {
  tolov_usuli: PaymentMethod | null;
  summa: string;
};

/** Ko'rsatish tartibi — formalar ham, hisobot ham shu tartibda. */
export const PAYMENT_METHODS: PaymentMethod[] = [
  "naqd",
  "terminal",
  "karta_otkazma",
  "bank",
];

/** Bitta usul guruhi; `usul: null` — noma'lum. */
export type PaymentMethodBucket = {
  gross_receipts?: string;
  refunds?: string;
  refund_count?: number;
  usul: PaymentMethod | null;
  soni: number;
  summa: string;
};

/**
 * Davr uchun kirgan pulning usullar bo'yicha bo'linishi.
 * `buckets` HAR DOIM to'liq (to'rt usul + noma'lum), `jami` — ularning
 * yig'indisi.
 */
export type PaymentMethodsOut = {
  gross_receipts?: string;
  refunds?: string;
  unresolved_returns_value?: string;
  unresolved_returns_count?: number;
  buckets: PaymentMethodBucket[];
  jami: string;
};

/**
 * `PATCH .../payment-status` tanasi — ish/sotuv/xarid uchun bir xil.
 * `tolov_usuli` — AYNAN shu to'lovning usuli (ixtiyoriy).
 */
export type PaymentStatusBody = {
  tolov_holati: PaymentStatus;
  tolangan_summa?: string | null;
  tolov_usuli?: PaymentMethod | null;
};
export type ProductHolat = "yetarli" | "kam" | "tugagan";
export type StockMovementType = "kirim" | "chiqim" | "tuzatish";

/**
 * Bitta tovarning bitta filialdagi qoldig'i.
 *
 * `holat` GLOBAL `min_ostatka`ga nisbatan hisoblanadi (server tomonda), ya'ni
 * "kam" chegarasi filialdan filialga sakramaydi. Tovar hech qachon kirmagan
 * filial ham ro'yxatda bo'ladi — `miqdor: "0"`, `holat: "tugagan"`. Aynan shu
 * "A da bor, B da yo'q" holatini ko'rsatish uchun kerak.
 */
export type ProductBranchLevel = {
  branch_id: number;
  branch_name: string;
  miqdor: string;
  is_active: boolean;
  holat: ProductHolat;
};

export type Product = {
  id: number;
  workshop_id: number;
  branch_id: number | null;
  nomi: string;
  artikul: string | null;
  kategoriya: string | null;
  birlik: string;
  barcode: string | null;
  tannarx: string;
  sotuv_narx: string;
  joriy_ostatka: string;
  min_ostatka: string;
  faol: boolean;
  holat: ProductHolat;
  /**
   * Faqat `include_branch_levels` so'ralganda to'ladi. `null` = so'ralmagan
   * (filial ustuni umuman chizilmaydi), `[]` = filialsiz ustaxona.
   */
  branch_levels: ProductBranchLevel[] | null;
  created_at: string;
  updated_at: string;
};

/**
 * Tovar analitikasidagi bir kanal. `profit` null — tannarx maskalangan.
 *
 * XARID kanalida `qty`/`value` ham null bo'lishi mumkin: uning summasi
 * tannarx, va `value / qty` orqali birlik tannarx chiqib ketardi. Sotuv
 * kanallarida ular doim to'lgan.
 */
export type ProductChannelStats = {
  qty: string | null;
  value: string | null;
  profit: string | null;
};

export type ProductAnalytics = {
  date_from: string;
  date_to: string;
  bucket: AnalyticsBucket;
  on_hand: string;
  min_ostatka: string;
  holat: ProductHolat;
  tannarx: string | null;
  sotuv_narx: string;
  margin_pct: number | null;
  sold: ProductChannelStats;
  sold_standalone: ProductChannelStats;
  sold_in_works: ProductChannelStats;
  purchased: ProductChannelStats;
  returned: ProductChannelStats;
  // Hisobdan chiqarilgan (yo'qotish) — `sold`ga ham, `series`ga ham kirmaydi.
  // Summasi tannarx bo'yicha, shuning uchun `purchased` kabi maskalanadi.
  hisobdan_chiqarilgan: ProductChannelStats;
  avg_sale_price: string | null;
  days_of_stock: number | null;
  last_movement_at: string | null;
  branch_levels: ProductBranchLevel[];
  series: { date: string; sold_qty: string; revenue: string; profit: string | null }[];
};

/**
 * Ombor signali. Qoida va chegaralar backend'da
 * (`services/product_signals.py`) — bu yerda faqat qiymatlar ro'yxati.
 *
 * `belgisiz` — zaxirasi ham, talabi ham yo'q tovar: hukm yo'q ("yaxshi" ham
 * emas, "qotgan" ham emas).
 */
export type ProductSignalKind =
  | "qotgan"
  | "sekin"
  | "yaxshi"
  | "tugayotgan"
  | "yangi"
  | "belgisiz";

export type ProductSignalBucket = {
  soni: number;
  /** Tannarx bo'yicha bog'langan pul — "qotgan pul" sarlavhasi shu. */
  qiymat: string;
  /** Davr ichidagi tushum — "tugayotgan" guruhda sarlavha shu. */
  sotuv_qiymati: string;
  /** Tannarxi yozilmagan tovarlar soni — `qiymat` shuncha kam ko'rsatadi. */
  narxsiz_soni: number;
};

export type ProductSignalRow = {
  product_id: number;
  nomi: string;
  birlik: string;
  kategoriya: string | null;
  faol: boolean;
  signal: ProductSignalKind;
  on_hand: string;
  min_ostatka: string;
  holat: ProductHolat;
  tannarx: string;
  qiymat: string;
  /** `null` = hech sotilmagan (cheksiz qoplash). */
  qoplash_kuni: number | null;
  sotilgan_miqdor: string;
  sotuv_qiymati: string;
  oxirgi_sotuv_at: string | null;
  birinchi_kirim_at: string | null;
  zaxira_kuni: number;
  /** Oxirgi xarid hujjati — yetkazib beruvchiga qaytarish shundan boshlanadi.
   *  `null` bo'lishi mumkin: `tuzatish` bilan kirgan zaxirada hujjat yo'q. */
  oxirgi_xarid_id: number | null;
};

export type ProductSignals = {
  window_days: number;
  baholangan: number;
  yangi_soni: number;
  qotgan: ProductSignalBucket;
  tugayotgan: ProductSignalBucket;
  sekin: ProductSignalBucket;
  yaxshi: ProductSignalBucket;
  belgisiz: ProductSignalBucket;
  items: ProductSignalRow[];
};

/**
 * Ombor ledgerining bitta satri — harakatlar jurnalining qatori.
 *
 * `tannarx_snapshot` tannarx ko'rmaydigan xodimda `null` (server maskalaydi).
 * Filial/xodim nomlari server tomonda `outerjoin` bilan keladi, shuning uchun
 * filialsiz eski satrda ular `null` — qator baribir ro'yxatda qoladi.
 * `purchase_id`/`sale_id`/`work_id`/`return_id`/`write_off_id` — manba hujjat;
 * `tuzatish` va bekor qilish kompensatsiyasida beshtasi ham `null`.
 */
export type StockMovement = {
  id: number;
  workshop_id: number;
  product_id: number;
  tur: StockMovementType;
  miqdor: string;
  birlik_narx: string;
  jami_summa: string;
  tannarx_snapshot: string | null;
  tolov_holati: PaymentStatus;
  tolangan_summa: string;
  izoh: string | null;
  user_id: number | null;
  user_name: string | null;
  branch_id: number | null;
  branch_name: string | null;
  branch_is_active: boolean | null;
  purchase_id: number | null;
  sale_id: number | null;
  work_id: number | null;
  return_id: number | null;
  write_off_id: number | null;
  created_at: string;
};

export type StockMovementResult = {
  movement: StockMovement;
  joriy_ostatka: string;
  ogohlantirish: string | null;
};

// ----- Xarid (purchase) hujjati -----
export type PurchaseLineOut = {
  movement_id: number;
  product_id: number;
  product_nomi: string;
  miqdor: string;
  birlik_narx: string;
  jami_summa: string;
};

export type Purchase = {
  original_total?: string;
  returned_total?: string;
  net_total?: string;
  debt_reduction_total?: string;
  cash_refund_total?: string;
  remaining_amount?: string;
  return_state?: "no_returns" | "partially_returned" | "fully_returned";

  id: number;
  workshop_id: number;
  branch_id: number | null;
  supplier_id: number | null;
  supplier_name: string | null;
  izoh: string | null;
  jami_summa: string;
  tolov_holati: PaymentStatus;
  tolangan_summa: string;
  tolov_usuli: PaymentMethod | null;
  payments: WorkPayment[];
  user_id: number | null;
  created_at: string;
  lines: PurchaseLineOut[];
};

// ----- Sotuv (sale) hujjati -----
// `jarayonda` — band qilingan, hali yakunlanmagan sotuv (Ombor → Jarayonda tabi).
export type SaleStatus = "active" | "cancelled" | "jarayonda";

export type SaleLineOut = {
  movement_id: number;
  product_id: number;
  product_nomi: string;
  miqdor: string;
  birlik_narx: string;
  tannarx_snapshot: string | null;
  jami_summa: string;
  satr_foyda: string;
};

export type Sale = {
  original_total?: string;
  returned_total?: string;
  net_total?: string;
  debt_reduction_total?: string;
  cash_refund_total?: string;
  remaining_amount?: string;
  return_state?: "no_returns" | "partially_returned" | "fully_returned";

  id: number;
  workshop_id: number;
  branch_id: number | null;
  client_id: number | null;
  client_name: string | null;
  seller_user_id: number | null;
  jami_summa: string;
  foyda: string;
  tolov_holati: PaymentStatus;
  tolangan_summa: string;
  tolov_usuli: PaymentMethod | null;
  payments: WorkPayment[];
  status: SaleStatus;
  izoh: string | null;
  created_at: string;
  lines: SaleLineOut[];
  ogohlantirishlar: string[];
};

// ----- Jarayondagi (band qilingan) sotuv -----
// Band qilishda mijoz tanlanmaydi (u tasdiqlashda aniqlanadi), shuning uchun
// bu shaklda mijoz maydoni yo'q. `jami_summa` — tovar kartochkasi narxidagi
// taxminiy qiymat.
export type JarayondaItem = {
  product_id: number;
  product_nomi: string;
  quantity: string;
};

export type JarayondaSale = {
  id: number;
  workshop_id: number;
  branch_id: number | null;
  status: SaleStatus;
  izoh: string | null;
  created_at: string;
  created_by_user_id: number | null;
  created_by_name: string | null;
  items: JarayondaItem[];
  jami_summa: string | null;
};

// ----- Qaytarish (return) hujjati -----
export type ReturnTypeT = "purchase_return" | "sale_return";

export type ReturnItemOut = {
  id: number;
  product_id: number;
  product_nomi: string;
  quantity: string;
  unit_price: string;
  jami_summa: string;
};

export type ReturnDoc = {
  id: number;
  workshop_id: number;
  return_type: ReturnTypeT;
  original_document_type: "purchase" | "sale";
  original_document_id: number;
  reason: string | null;
  status: "completed";
  jami_summa: string;
  user_id: number | null;
  created_at: string;
  items: ReturnItemOut[];
  // Faqat detal (GET /returns/{id}) va yaratish javobida to'ladi; ro'yxatda null.
  stok_ozgarishi: "chiqim" | "kirim" | null;
  balans_ozgarishi: string | null;
  ogohlantirishlar: string[];
};

export type DebtClient = {
  client_id: number;
  ism: string;
  telefon: string;
  plates: string[];
  balans: string;
  oxirgi_harakat: string | null;
  reminder_status: "pending" | "delivered_unpaid" | "extended" | "paid" | "telegram_unlinked";
  reminder_text: string;
  telegram_linked: boolean;
  debt_due_date: string | null;
};

export type DebtSource = "work" | "sale" | "purchase" | "qolda";

export type DebtTransaction = {
  id: number;
  workshop_id: number;
  client_id: number | null;
  debtor_name: string | null;
  tur: "qarz" | "tolov";
  summa: string;
  manba: DebtSource;
  manba_id: number | null;
  /** Faqat pul haqiqatan harakat qilgan "tolov" qatorlarida to'ladi. */
  tolov_usuli: PaymentMethod | null;
  izoh: string | null;
  user_id: number | null;
  created_at: string;
};

// Birlashtirilgan qarz daftari qatori (sotuv/xarid/ish bo'yicha guruhlangan).
export type DebtLedgerRow = {
  source_type: DebtSource;
  source_ref_id: number | null;
  /** Faqat `qolda` qatorlarida: pul kirdimi (tolov) yoki teskari yozuvmi (qarz). */
  tur?: "qarz" | "tolov" | null;
  /**
   * Qarzdorning UMUMIY balansi yopilganmi. True bo'lsa hujjatdagi qoldiq —
   * TARIX, ochiq talab emas (mijoz balans bo'yicha hisoblashgan).
   */
  hisob_yopilgan: boolean;
  // Qarama-qarshi tomon: debitorlik (sotuv/ish) qatorida QARZDOR mijoz,
  // kreditorlik (xarid) qatorida biz qarzdor bo'lgan KREDITOR (yetkazib beruvchi).
  counterparty_name: string | null;
  summa: string; // qoldiq (remaining)
  jami: string; // hujjatning umumiy summasi (total)
  holat: PaymentStatus;
  created_at: string;
  updated_at: string | null;
};

export type DebtLedgerOut = {
  rows: DebtLedgerRow[];
  // ⚠️ Bu jami raqamlar QATORLARDAN hisoblanmaydi — ular daftar netidan
  // keladi (`/debts/summary`, `/owner/kpi` bilan AYNAN bir xil). Qatorlarni
  // qo'shib jami chiqarish ikkinchi, kelishmaydigan raqam bo'lardi.
  jami_debitorlik: string;
  jami_kreditorlik: string;
};

export type DebtClientHistory = {
  client_id: number;
  ism: string;
  telefon: string;
  balans: string;
  harakatlar: DebtTransaction[];
};

export type DebtSummary = {
  jami_debitorlik: string;
  qarzdor_mijozlar_soni: number;
};

/** Kartochkasiz qarzdor — anonim kredit sotuvdan tug'iladi. */
export type DebtNamedDebtor = {
  debtor_name: string;
  balans: string;
  oxirgi_harakat: string | null;
};

export type DebtNamedDebtorHistory = {
  debtor_name: string;
  balans: string;
  harakatlar: DebtTransaction[];
};

export type DebtTolovBody = {
  client_id?: number;
  debtor_name?: string;
  summa: string;
  tolov_usuli?: PaymentMethod | null;
  izoh?: string | null;
  /** Ortiqcha to'lov (avans) uchun majburiy tasdiq — server sukut bo'yicha rad etadi. */
  avans_tasdiqlandi?: boolean;
};

export type DebtTolovResult = {
  harakat: DebtTransaction;
  oldingi_balans: string;
  balans: string;
};

// ----- Hisobdan chiqarish (spisaniye) — sababli ombor chiqimi -----
export type WriteOffReason =
  | "singan"
  | "muddati_otgan"
  | "yoqolgan"
  | "ichki_istemol"
  | "inventarizatsiya";

export type WriteOffLineOut = {
  product_id: number;
  product_nomi: string;
  birlik: string;
  miqdor: string;
  // Yo'qotish QIYMATI faqat egasi uchun — xodimda null (fmtMoney → "—").
  tannarx: string | null;
  jami_tannarx: string | null;
};

export type WriteOff = {
  id: number;
  workshop_id: number;
  branch_id: number | null;
  sabab: WriteOffReason;
  izoh: string | null;
  user_id: number | null;
  created_at: string;
  branch_name: string | null;
  branch_is_active: boolean | null;
  user_name: string | null;
  jami_tannarx: string | null; // tannarx maskalangan bo'lsa null
  lines: WriteOffLineOut[];
  ogohlantirishlar: string[];
};

// ----- Yetkazib beruvchi (supplier) — katalog + kreditorlik daftari -----
export type Supplier = {
  id: number;
  workshop_id: number;
  name: string;
  phone: string | null;
  contact_person: string | null;
  address: string | null;
  note: string | null;
  faol: boolean;
  created_at: string;
};

/** Kreditorlik ro'yxati qatori — balans bo'yicha kamayish tartibida keladi. */
export type PayableSupplier = {
  supplier_id: number;
  nomi: string;
  telefon: string | null;
  aloqa_shaxsi: string | null;
  faol: boolean;
  balans: string; // musbat = biz qarzdormiz; manfiy = avans
  oxirgi_harakat: string | null;
};

export type SupplierTransaction = {
  id: number;
  workshop_id: number;
  supplier_id: number;
  tur: "qarz" | "tolov";
  summa: string;
  manba: DebtSource;
  manba_id: number | null;
  /** Faqat pul haqiqatan harakat qilgan "tolov" qatorlarida to'ladi. */
  tolov_usuli: PaymentMethod | null;
  izoh: string | null;
  user_id: number | null;
  created_at: string;
};

export type SupplierHistory = {
  supplier_id: number;
  nomi: string;
  telefon: string | null;
  aloqa_shaxsi: string | null;
  manzil: string | null;
  izoh: string | null;
  faol: boolean;
  balans: string;
  harakatlar: SupplierTransaction[];
};

export type PayableSummary = {
  jami_kreditorlik: string;
  qarzdor_yetkazib_beruvchilar_soni: number;
};

export type WorkRow = {
  id: number;
  created_at: string;
  client_id: number;
  client_name: string;
  car_id: number;
  plate_number: string;
  service_id: number;
  service_name: string;
  user_id: number;
  master_name: string;
  branch_id: number | null;
  price: string;
  parts_total: string;
  tolov_holati: PaymentStatus;
  tolangan_summa: string;
  tolov_usuli: PaymentMethod | null;
  status: SaleStatus;
  km: number;
  next_service_km?: number | null;
  note?: string | null;
  services: Array<{ service_id: number; name: string; price: string }>;
  mechanics: Array<{ user_id: number; full_name: string }>;
  payments: WorkPayment[];
};

/** Joriy filtrdagi to'lanmagan/qisman ishlar soni va jami qoldiq. */
export type WorksPaymentSummary = {
  // Qoldiqlar yig'indisi ATAYLAB yo'q — izohni backend schema'sida qarang.
  count: number;
};

// ----- Ish (work) tafsiloti — inline ochilganda ko'rsatiladi -----
export type WorkDetail = {
  id: number;
  branch_id: number | null;
  car_id: number;
  user_id: number;
  service_id: number;
  service_name: string | null;
  price: string;
  km: number | null;
  next_service_km: number | null;
  note: string | null;
  tolov_holati: PaymentStatus;
  tolangan_summa: string;
  tolov_usuli: PaymentMethod | null;
  parts_total: string;
  status: SaleStatus;
  created_at: string;
  parts: SaleLineOut[];
  services: Array<{ service_id: number; name: string; price: string }>;
  mechanics: Array<{ user_id: number; full_name: string }>;
  payments: WorkPayment[];
};

export type Page<T> = { items: T[]; total: number; page: number; page_size: number };

// ----- Murojaatlar (support tickets) -----
export type TicketType = "support" | "feedback";
export type TicketStatus = "new" | "seen" | "replied";

export type SupportTicket = {
  id: number;
  type: TicketType;
  title: string;
  body: string;
  status: TicketStatus;
  admin_reply: string | null;
  replied_at: string | null;
  created_at: string;
};

export type WorkshopTicket = SupportTicket & {
  sender: { id: number; name: string; role: "owner" | "employee" };
};

export type CarHistoryItem = {
  work_id: number;
  created_at: string;
  service_name: string;
  km: number;
  price: string;
  master_name: string;
};

export type CarWithHistory = {
  id: number;
  plate_number: string;
  model: string | null;
  year: number | null;
  km: number;
  next_due_at: string | null;
  history: CarHistoryItem[];
};

// Mijozning qiymati/faolligi. Definitsiyalar backendda:
// `app/services/client_metrics.py` (aylanma = bekor qilinmagan ish + sotuv,
// qaytarish ayiriladi; tashrif = hujjat; o'rtacha = aylanma / tashrif).
//
// `balans` — FAQAT qarz daftari bor tarifda (standard+). Tarifda yo'q bo'lsa
// backend maydonni UMUMAN yubormaydi ("yashirilgan = mavjud emas"), shuning
// uchun tur `?:` — `null` emas. UI shu bo'yicha ustunni butunlay yashiradi.
export type ClientMetrics = {
  volume: string;
  visits: number;
  avg_transaction: string;
  customer_since: string;
  last_seen: string | null;
  balans?: string;
};

export type ClientDetails = {
  id: number;
  full_name: string;
  phone: string;
  telegram_id: number | null;
  cars: CarWithHistory[];
  // `/owner/clients/{id}` har doim qaytaradi; `searchClients` — yo'q.
  metrics?: ClientMetrics;
};

export type ClientOverviewRow = ClientMetrics & {
  id: number;
  full_name: string;
  phone: string;
  telegram_id: number | null;
  plates: string[];
};

export type ClientSort =
  | "volume"
  | "visits"
  | "avg"
  | "last_seen"
  | "customer_since"
  | "name"
  | "balans";

export type ClientOverviewParams = {
  q?: string;
  has_telegram?: boolean;
  not_seen_days?: number;
  branch_id?: number;
  sort?: ClientSort;
  order?: "asc" | "desc";
  page?: number;
  page_size?: number;
};

export type ManualMessageResult = {
  log_id: number | null;
  status: "sent" | "failed";
  blocked: boolean;
  sent_at: string | null;
};

export type ManualMessageRow = {
  id: number;
  created_at: string;
  sent_at: string | null;
  status: string;
  text: string;
  user_name: string | null;
  blocked: boolean;
};

// --- Ommaviy tarqatish (broadcast / kampaniya) ---
export type BroadcastSegment = "all" | "debtors" | "lapsed";
export type CampaignStatus =
  | "queued"
  | "running"
  | "completed"
  | "stopped"
  | "failed";

export type BroadcastPreview = {
  targeted: number;
  linked: number;
  unlinked: number;
  opted_out: number;
  capped: number;
  will_receive: number;
};

export type CampaignRow = {
  id: number;
  campaign_uuid: string;
  segment: BroadcastSegment;
  lapsed_months: number | null;
  template_key: string | null;
  status: CampaignStatus;
  text: string;
  targeted: number;
  reachable: number;
  sent: number;
  failed: number;
  skipped_opted_out: number;
  skipped_capped: number;
  skipped_unlinked: number;
  skipped_budget: number;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
};

export type CampaignDetail = CampaignRow & { user_name: string | null };

export type EmployeeRow = {
  employment_start?: string | null;
  employment_end?: string | null;
  id: number;
  full_name: string;
  phone: string;
  branch_id: number | null;
  works_count: number;
  total_sum: string;
  can_be_usta: boolean;
  can_be_sotuvchi: boolean;
  can_be_omborchi: boolean;
  faol: boolean;
};

export type ServiceOut = {
  id: number;
  name: string;
  code: string;
  narx: string | null;
  izoh: string | null;
  interval_km: number | null;
  interval_months: number | null;
  sms_text: string | null;
  is_active: boolean;
};

export type ReminderLogRow = {
  id: number;
  created_at: string;
  due_at: string | null;
  sent_at: string | null;
  channel: "sms" | "telegram";
  status: string;
  client_id: number;
  client_name: string;
  client_telegram_id: number | null;
  text: string | null;
  source: "reminder" | "sms_log";
};

export type Tariff = "trial" | "standard" | "comfort" | "pro" | "max";

export type PlanLimits = {
  max_users: number | null;
  max_branches: number | null;
  max_works: number | null;
  current_users: number;
  current_branches: number;
  current_works: number;
};

export type TariffPlanInfo = {
  tariff: Tariff;
  max_users: number | null;
  max_branches: number | null;
  max_works: number | null;
  features: string[];
  price: number | null;
};

export type TariffStatus = {
  tariff: Tariff;
  status: string;
  trial_end: string | null;
  is_expired: boolean;
  features: string[];
  limits: PlanLimits | null;
  plans: TariffPlanInfo[];
};

export type BranchOut = {
  id: number;
  name: string;
  address: string | null;
  telefon: string | null;
  is_active: boolean;
  faol_ustalar: number;
  // Ommaviy profil — ustaxona profili bilan bir xil maydonlar, filial kesimida.
  latitude: number | null;
  longitude: number | null;
  description: string | null;
  work_hours: string | null;
  instagram: string | null;
  telegram: string | null;
  website: string | null;
};

export type BranchArchiveResult = { ok: boolean; warning: string | null };

export type WorkshopSettings = {
  name: string;
  phone: string | null;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  location_url: string | null;
  description: string | null;
  work_hours: string | null;
  instagram: string | null;
  telegram: string | null;
  website: string | null;
};

export type FeedbackItem = {
  id: number;
  stars: number;
  comment: string | null;
  date: string;
};

export type FeedbackOverview = {
  average: number | null;
  count: number;
  distribution: Record<string, number>;
  items: FeedbackItem[];
};

export type AnalyticsPeriod = "day" | "week" | "month" | "quarter" | "year";

/** Bucket darajasi. `hour` faqat "Bugun" davrida (kalitlari mahalliy vaqtda). */
export type AnalyticsBucket = "hour" | "day" | "week" | "month";

/**
 * KPI o'lchovlari — karusel kartalari ham, jadval ustunlari ham shu
 * maydonlardan yasaladi (yagona deskriptor ro'yxati orqali).
 *
 * Pul/miqdor maydonlari — Decimal, satr sifatida keladi.
 */
export type KpiMetrics = {
  net_revenue: string;
  total_revenue: string;
  profit: string;
  avg_transaction: string;
  products_qty: string;
  products_revenue: string;
  services_count: number;
  services_revenue: string;
  returns_count: number;
  returns_value: string;
  new_clients: number;
  returning_clients: number;
};

export type KpiMetricKey = keyof KpiMetrics;

export type AnalyticsKpis = {
  period: {
    period: AnalyticsPeriod;
    date_from: string;
    date_to: string;
    prev_from: string;
    prev_to: string;
    bucket: AnalyticsBucket;
  };
  group_by: AnalyticsBucket;
  current: KpiMetrics & { gross_products_revenue?: string; linked_returns_value?: string; unresolved_returns_value?: string };
  previous: KpiMetrics & { gross_products_revenue?: string; linked_returns_value?: string; unresolved_returns_value?: string };
  /** Kalit = `KpiMetrics` maydon nomi; baza 0 bo'lsa null (aniqlanmagan). */
  change_pct: Partial<Record<KpiMetricKey, number | null>>;
  buckets: { date: string; metrics: KpiMetrics }[];
};

export type BranchComparisonRow = {
  branch_id: number | null;
  branch_name: string;
  is_active: boolean;
  daromad: string;
  ish_soni: number;
  ortacha_chek: string;
  top_usta_id: number | null;
  top_usta_name: string | null;
  top_usta_daromad: string;
};

export type BranchComparison = {
  date_from: string;
  date_to: string;
  jami_daromad: string;
  jami_ish_soni: number;
  rows: BranchComparisonRow[];
};

export type Analytics = {
  period: {
    period: AnalyticsPeriod;
    date_from: string;
    date_to: string;
    prev_from: string;
    prev_to: string;
    bucket: AnalyticsBucket;
  };
  summary: {
    works_count: number;
    prev_works_count: number;
    works_change_pct: number | null;
    avg_check: string;
    prev_avg_check: string;
    avg_check_change_pct: number | null;
    active_clients: number;
    prev_active_clients: number;
    active_clients_change_pct: number | null;
  };
  revenue: {
    total: string;
    prev_total: string;
    change_pct: number | null;
    series: { date: string; amount: string }[];
    prev_series: { date: string; amount: string }[];
  };
  services: { service_id: number; name: string; count: number; revenue: string }[];
  employees: {
    user_id: number;
    full_name: string;
    works_count: number;
    /** Faqat ish haqi (Work.price) — tovarlar `parts_revenue`da. */
    revenue: string;
    parts_revenue: string;
    total_revenue: string;
    avg_check: string;
    clients_count: number;
    share_pct: number;
  }[];
  clients: {
    new_count: number;
    returning_count: number;
    with_telegram: number;
    without_telegram: number;
    telegram_pct: number;
    linked_via_qr_30d: number;
  };
  reminders: { sent: number; pending: number; failed: number };
  heatmap: { weekday: number; hour: number; count: number; revenue: string }[];
  top_clients: { client_id: number; full_name: string; works_count: number; revenue: string }[];
  reminder_funnel: {
    created: number;
    sent: number;
    failed: number;
    pending: number;
    success_pct: number;
  } | null;
  products: {
    product_id: number;
    nomi: string;
    qty: string;
    revenue: string;
    profit: string | null;
  }[];
};

/** Tovar tahlili jadvali qatori — `GET /owner/analytics/products`. */
export type AnalyticsProductRow = {
  product_id: number;
  nomi: string;
  birlik: string;
  kategoriya: string | null;
  qty: string;
  revenue: string;
  profit: string;
  margin_pct: number | null;
  share_pct: number;
};

export type AnalyticsProducts = {
  period: Analytics["period"];
  items: AnalyticsProductRow[];
  totals: {
    qty: string;
    revenue: string;
    profit: string;
    margin_pct: number | null;
    /** Limitdan mustaqil — davrda sotilgan har xil tovar soni. */
    products_count: number;
  };
};

export type FinanceAllocationBody = {
  collection_id: number; work_id?: number; sale_id?: number;
  amount: string; reason: string; operation_key: string;
};
export type FinanceCollections = {
  collections: { id: number; unallocated: string; amount: string; credit: boolean; consumed?: string; excess?: string; unresolved?: boolean }[];
  documents: { id: number; kind: "work" | "sale"; outstanding: string; allocated: string; original_total?: string; net_total?: string; capacity?: string; excess?: string; unresolved?: boolean }[];
  allocations: { id: number; collection_id: number; work_id: number | null; sale_id: number | null;
    amount: string; reverses_id: number | null; reason: string }[];
};
export const api = {
  kassaDaily: (id: number, day: string) => req<{ inflow: string; outflow: string; noncash: Record<string, { in: string; out: string }> }>(`/finance/kassa/branches/${id}/daily?day=${encodeURIComponent(day)}`),
  kassaAccess: () => req<{ allowed: boolean; branches: { id: number; name: string }[] }>("/finance/kassa/access"),
  kassaBranch: (id: number, before?: number) => req<CashReport>(`/finance/kassa/branches/${id}${before ? `?before=${before}` : ""}`),
  kassaSession: (id: number) => req<CashSession>(`/finance/kassa/sessions/${id}`),
  kassaCashiers: () => req<{ employee_ids: number[] }>("/finance/kassa/cashiers"),
  profitLoss: (query = "") => req<ProfitLossReport>(`/finance/profit-loss?${query}`),
  financeStatus: () => req<FinanceStatus>("/finance/status"),
  financeActivate: (module: string, data: Record<string, unknown>) => req<FinanceStatus>(`/finance/modules/${module}`, { method: "PUT", body: JSON.stringify(data) }),
  payroll: (query = "") => req<PayrollReport>(`/finance/payroll?${query}`),
  expenses: (query = "") => req<ExpenseReport>(`/finance/expenses?${query}`),
  financeCommand: (path: string, data: Record<string, unknown>, method = "POST") => req<{ id: number }>(`/finance/${path}`, { method, body: JSON.stringify(data) }),
  financeCollections: (clientId: number) => req<FinanceCollections>(`/finance/collections/${clientId}`),
  financeAllocate: (data: FinanceAllocationBody) => req<{ id: number }>("/finance/allocations", { method: "POST", body: JSON.stringify(data) }),
  financeRelease: (id: number, reason: string) => req<{ id: number }>(`/finance/allocations/${id}/release`, { method: "POST", body: JSON.stringify({ reason }) }),
  // Logout must not trigger req()'s credential-clearing error side effects.
  async logout(): Promise<void> {
    const r = await fetch(`${BASE}/auth/logout`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (r.status !== 200 || (await r.json()).ok !== true) {
      throw new Error("logout_failed");
    }
  },
  login: (identifier: string, password: string) =>
    req<TokenOut>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ identifier, password }),
    }),
  me: () => req<UserOut>("/auth/me"),
  changePassword: (
    current_password: string,
    new_password: string,
    confirm_password?: string,
  ) =>
    req<UserOut>("/auth/change-password", {
      method: "POST",
      body: JSON.stringify({
        current_password,
        new_password,
        ...(confirm_password !== undefined ? { confirm_password } : {}),
      }),
    }),
  kpi: (branchId?: number | null) =>
    req<Kpi>(`/owner/kpi${branchId != null ? `?branch_id=${branchId}` : ""}`),
  works: (params: Record<string, string | number | undefined>) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== "" && v !== null) q.set(k, String(v));
    }
    return req<Page<WorkRow>>(`/owner/works?${q.toString()}`);
  },
  // Ish jurnalidagi AYNAN o'sha filtr bo'yicha to'lanmagan/qisman xulosasi.
  // `qarzlar` bo'lmagan tarifda 403 — chaqiruvchi feature bilan gate qiladi.
  worksPaymentSummary: (params: Record<string, string | number | undefined>) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== "" && v !== null) q.set(k, String(v));
    }
    return req<WorksPaymentSummary>(`/owner/works/payment-summary?${q.toString()}`);
  },
  // Ish jurnalidagi AYNAN o'sha filtr bo'yicha "pul qanday kirdi".
  // `payment-summary` dan farqli — tarif bilan cheklanmagan (403 bo'lmaydi).
  worksPaymentMethods: (
    params: Record<string, string | number | undefined>,
  ) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== "" && v !== null) q.set(k, String(v));
    }
    return req<PaymentMethodsOut>(
      `/owner/works/payment-methods?${q.toString()}`,
    );
  },
  searchClients: (q: string, hasTelegram?: boolean) => {
    const sp = new URLSearchParams();
    if (q) sp.set("q", q);
    if (hasTelegram !== undefined) sp.set("has_telegram", hasTelegram ? "true" : "false");
    const qs = sp.toString();
    return req<ClientDetails[]>(`/owner/clients/search${qs ? `?${qs}` : ""}`);
  },
  // Metrikali mijozlar ro'yxati — saralash/filtr/sahifalash SERVER tomonida
  // (`searchClients` dan farqli: u har bir mijoz uchun to'liq tarixni yuklaydi).
  clientsOverview: (p: ClientOverviewParams) => {
    const sp = new URLSearchParams();
    if (p.q) sp.set("q", p.q);
    if (p.has_telegram !== undefined)
      sp.set("has_telegram", p.has_telegram ? "true" : "false");
    if (p.not_seen_days !== undefined)
      sp.set("not_seen_days", String(p.not_seen_days));
    if (p.branch_id !== undefined) sp.set("branch_id", String(p.branch_id));
    if (p.sort) sp.set("sort", p.sort);
    if (p.order) sp.set("order", p.order);
    sp.set("page", String(p.page ?? 1));
    sp.set("page_size", String(p.page_size ?? 20));
    return req<Page<ClientOverviewRow>>(
      `/owner/clients/overview?${sp.toString()}`,
    );
  },
  botInfo: () => req<{ username: string }>("/owner/bot-info"),
  clientDetails: (id: number) => req<ClientDetails>(`/owner/clients/${id}`),
  employees: (from?: string, to?: string) => {
    const q = new URLSearchParams();
    if (from) q.set("date_from", from);
    if (to) q.set("date_to", to);
    return req<EmployeeRow[]>(`/owner/employees?${q.toString()}`);
  },
  createEmployee: (data: Record<string, unknown>) =>
    req("/owner/employees", { method: "POST", body: JSON.stringify(data) }),
  updateEmployee: (id: number, data: Record<string, unknown>) =>
    req(`/owner/employees/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteEmployee: (id: number) =>
    req(`/owner/employees/${id}`, { method: "DELETE" }),
  services: (includeInactive = false) =>
    req<ServiceOut[]>(`/services?include_inactive=${includeInactive}`),
  createService: (data: Record<string, unknown>) =>
    req("/services", { method: "POST", body: JSON.stringify(data) }),
  updateService: (id: number, data: Record<string, unknown>) =>
    req(`/services/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteService: (id: number) =>
    req(`/services/${id}`, { method: "DELETE" }),
  reminders: (params: Record<string, string | undefined>) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v) q.set(k, v);
    }
    return req<Page<ReminderLogRow>>(`/owner/reminders?${q.toString()}`);
  },
  sendReminderNow: (id: number) =>
    req<{ reminder_id: number; status: string; message: string }>(
      `/owner/reminders/${id}/send`,
      { method: "POST" },
    ),
  sendManualMessage: (
    clientId: number,
    body: { text: string; send_uuid: string; template_key?: string },
  ) =>
    req<ManualMessageResult>("/messaging/send", {
      method: "POST",
      body: JSON.stringify({ client_id: clientId, ...body }),
    }),
  manualMessageHistory: (clientId: number) =>
    req<ManualMessageRow[]>(`/messaging/history?client_id=${clientId}`),
  // Ommaviy tarqatish (broadcast) — egasi-only, comfort+.
  broadcastPreview: (body: {
    segment: BroadcastSegment;
    lapsed_months?: number | null;
  }) =>
    req<BroadcastPreview>("/broadcast/preview", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  createCampaign: (body: {
    segment: BroadcastSegment;
    text: string;
    campaign_uuid: string;
    lapsed_months?: number | null;
    template_key?: string | null;
  }) =>
    req<CampaignRow>("/broadcast/campaigns", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  listCampaigns: () => req<CampaignRow[]>("/broadcast/campaigns"),
  campaignDetail: (id: number) =>
    req<CampaignDetail>(`/broadcast/campaigns/${id}`),
  tariff: () => req<TariffStatus>("/owner/tariff"),
  activate: (code: string) =>
    req<TariffStatus>("/owner/activate", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),
  branches: () => req<BranchOut[]>("/owner/branches"),
  createBranch: (data: Record<string, unknown>) =>
    req<BranchOut>("/owner/branches", { method: "POST", body: JSON.stringify(data) }),
  updateBranch: (id: number, data: Record<string, unknown>) =>
    req<BranchOut>(`/owner/branches/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  // Arxivlash (soft delete) — zaxira qolsa { warning } qaytadi.
  deleteBranch: (id: number) =>
    req<BranchArchiveResult>(`/owner/branches/${id}`, { method: "DELETE" }),
  analytics: (period: AnalyticsPeriod, branchId?: number | null) =>
    req<Analytics>(
      `/owner/analytics?period=${period}${branchId != null ? `&branch_id=${branchId}` : ""}`,
    ),
  analyticsComparison: (period: AnalyticsPeriod) =>
    req<BranchComparison>(`/owner/analytics/comparison?period=${period}`),
  /**
   * KPI karusel + jadval. `/owner/analytics`dan ALOHIDA so'rov: jadval
   * guruhlanishini o'zgartirganda og'ir analitika yukini (heatmap, series,
   * top-5 ro'yxatlari) qayta tortmaslik kerak.
   */
  analyticsKpis: (
    period: AnalyticsPeriod,
    groupBy?: AnalyticsBucket,
    branchId?: number | null,
  ) => {
    const q = new URLSearchParams({ period });
    if (groupBy) q.set("group_by", groupBy);
    if (branchId != null) q.set("branch_id", String(branchId));
    return req<AnalyticsKpis>(`/owner/analytics/kpis?${q.toString()}`);
  },
  /**
   * Tovar tahlili jadvali — davr bo'yicha tovar-sotuv reytingi (sotuv + ishga
   * biriktirilgan chiqimlar, KPI karusel bilan bir xil qamrov). `limit`
   * berilmaydi — backend default (50) mahsulot qarori.
   */
  analyticsProducts: (period: AnalyticsPeriod, branchId?: number | null) => {
    const q = new URLSearchParams({ period });
    if (branchId != null) q.set("branch_id", String(branchId));
    return req<AnalyticsProducts>(`/owner/analytics/products?${q.toString()}`);
  },
  workshopInviteLink: () =>
    req<{ token: string; deep_link: string }>("/owner/workshop-invite-link"),
  workshopSettings: () => req<WorkshopSettings>("/owner/workshop"),
  updateWorkshopSettings: (data: Partial<WorkshopSettings>) =>
    req<WorkshopSettings>("/owner/workshop", {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  feedbacks: () => req<FeedbackOverview>("/owner/feedbacks"),

  // ----- Ishlar (yaratish/tahrir/tafsilot) -----
  createWork: (data: Record<string, unknown>) =>
    req<WorkRow>("/works", { method: "POST", body: JSON.stringify(data) }),
  getWork: (id: number) => req<WorkDetail>(`/works/${id}`),
  updateWorkPayment: (
    id: number,
    data: PaymentStatusBody,
  ) =>
    req<WorkRow>(`/works/${id}/payment-status`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  /**
   * Ishning to'lov USULINI to'g'rilaydi — `PATCH /works/{id}` tahriri.
   * Yonidagi `payment-status` dan farqi: u pul harakatini yozadi va usulni
   * faqat to'langan summa OSHGANDA yangilaydi, ya'ni to'liq to'langan ishdagi
   * xato usul u yerdan tuzatilmaydi.
   *
   * Javob `WorkOut` — unda `client_name`/`plate_number`/`master_name` YO'Q,
   * shuning uchun `WorkRow` deb tiplanmaydi; UI ga faqat yangi usul kerak.
   */
  correctWorkMethod: (id: number, usul: PaymentMethod | null) =>
    req<{ id: number; tolov_usuli: PaymentMethod | null }>(`/works/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ tolov_usuli: usul }),
    }),
  correctWorkPayments: (
    id: number,
    payments: Array<{ tolov_usuli: PaymentMethod; summa: string }>,
  ) =>
    req<{ id: number; tolov_usuli: PaymentMethod | null; payments: WorkPayment[] }>(`/works/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ payments }),
    }),

  // ----- Mijoz/avto yaratish (ish formasida "Mijoz qo'shish") -----
  createClient: (data: { full_name: string; phone: string }) =>
    req<{ id: number; full_name: string; phone: string }>("/clients", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  updateClient: (id: number, data: { full_name?: string; phone?: string }) =>
    req<{ id: number; full_name: string; phone: string }>(`/clients/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  createCar: (data: {
    client_id: number;
    plate_number: string;
    model?: string | null;
    year?: number | null;
    km?: number;
  }) =>
    req<{ id: number; plate_number: string }>("/cars", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  updateCar: (
    id: number,
    data: { plate_number?: string; model?: string | null; year?: number | null; km?: number },
  ) =>
    req<{ id: number; plate_number: string }>(`/cars/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  deleteCar: (id: number) => req(`/cars/${id}`, { method: "DELETE" }),

  // ----- Tovarlar -----
  products: (
    params: {
      faol?: boolean;
      kategoriya?: string;
      kam_qolgan?: boolean;
      branch_id?: number | null;
      /**
       * Har bir tovarga filiallar kesimini qo'shadi. Ro'yxat sahifalanmaydi,
       * ya'ni javob tovarlar × filiallar bo'lib o'sadi — faqat filiali bir
       * nechta bo'lgan ustaxonada so'raladi.
       */
      include_branch_levels?: boolean;
    } = {},
  ) => {
    const q = new URLSearchParams();
    if (params.faol !== undefined) q.set("faol", String(params.faol));
    if (params.kategoriya) q.set("kategoriya", params.kategoriya);
    if (params.kam_qolgan) q.set("kam_qolgan", "true");
    if (params.branch_id != null) q.set("branch_id", String(params.branch_id));
    if (params.include_branch_levels) q.set("include_branch_levels", "true");
    const qs = q.toString();
    return req<Product[]>(`/products${qs ? `?${qs}` : ""}`);
  },
  productByBarcode: (barcode: string) =>
    req<Product>(`/products/barcode/${encodeURIComponent(barcode)}`),
  createProduct: (data: Record<string, unknown>) =>
    req<Product>("/products", { method: "POST", body: JSON.stringify(data) }),
  updateProduct: (id: number, data: Record<string, unknown>) =>
    req<Product>(`/products/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  stockTuzatish: (productId: number, data: Record<string, unknown>) =>
    req<StockMovementResult>(`/products/${productId}/movements/tuzatish`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  /**
   * Ombor signallari — "qancha pul qotib qolgan / nima tugayapti".
   * Faqat egasi (backend'da 403 xodimga). `limit=0` — faqat yig'indi.
   */
  productSignals: (
    params: {
      branch_id?: number | null;
      signal?: ProductSignalKind;
      limit?: number;
    } = {},
  ) => {
    const q = new URLSearchParams();
    if (params.branch_id != null) q.set("branch_id", String(params.branch_id));
    if (params.signal) q.set("signal", params.signal);
    if (params.limit != null) q.set("limit", String(params.limit));
    const qs = q.toString();
    return req<ProductSignals>(`/products/signals${qs ? `?${qs}` : ""}`);
  },
  /** Bitta tovarning davr kesimidagi analitikasi (qatorga bosilganda). */
  productAnalytics: (
    productId: number,
    period: AnalyticsPeriod,
    branchId?: number | null,
  ) => {
    const q = new URLSearchParams({ period });
    if (branchId != null) q.set("branch_id", String(branchId));
    return req<ProductAnalytics>(`/products/${productId}/analytics?${q.toString()}`);
  },
  /** Tovar harakatlari jurnali — xom ledger (agregat analitika emas). */
  productMovements: (
    productId: number,
    params: {
      page?: number;
      page_size?: number;
      tur?: StockMovementType | "";
      branch_id?: number | null;
      date_from?: string;
      date_to?: string;
    } = {},
  ) => {
    const q = new URLSearchParams();
    if (params.page) q.set("page", String(params.page));
    if (params.page_size) q.set("page_size", String(params.page_size));
    if (params.tur) q.set("tur", params.tur);
    if (params.branch_id != null) q.set("branch_id", String(params.branch_id));
    if (params.date_from) q.set("date_from", params.date_from);
    if (params.date_to) q.set("date_to", params.date_to);
    const qs = q.toString();
    return req<Page<StockMovement>>(
      `/products/${productId}/movements${qs ? `?${qs}` : ""}`,
    );
  },

  // ----- Xaridlar (purchase hujjati) -----
  purchases: (params: Record<string, string | number | undefined> = {}) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== "" && v !== null) q.set(k, String(v));
    }
    return req<Page<Purchase>>(`/purchases?${q.toString()}`);
  },
  createPurchase: (data: Record<string, unknown>) =>
    req<Purchase>("/purchases", { method: "POST", body: JSON.stringify(data) }),
  getPurchase: (id: number) => req<Purchase>(`/purchases/${id}`),
  updatePurchasePayment: (
    id: number,
    data: PaymentStatusBody,
  ) =>
    req<Purchase>(`/purchases/${id}/payment-status`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  /** Xaridning to'lov USULINI to'g'rilaydi (`PaymentMethodCorrection`). */
  correctPurchaseMethod: (id: number, usul: PaymentMethod | null) =>
    req<Purchase>(`/purchases/${id}/tolov-usuli`, {
      method: "PATCH",
      body: JSON.stringify({ tolov_usuli: usul }),
    }),

  // ----- Sotuvlar (sale hujjati) -----
  sales: (params: Record<string, string | number | undefined> = {}) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== "" && v !== null) q.set(k, String(v));
    }
    return req<Page<Sale>>(`/sales?${q.toString()}`);
  },
  salesPaymentMethods: (
    params: Record<string, string | number | undefined> = {},
  ) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== "" && v !== null) q.set(k, String(v));
    }
    return req<PaymentMethodsOut>(`/sales/payment-methods?${q.toString()}`);
  },
  createSale: (data: Record<string, unknown>) =>
    req<Sale>("/sales", { method: "POST", body: JSON.stringify(data) }),
  getSale: (id: number) => req<Sale>(`/sales/${id}`),
  updateSalePayment: (
    id: number,
    data: PaymentStatusBody,
  ) =>
    req<Sale>(`/sales/${id}/payment-status`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  /** Sotuvning to'lov USULINI to'g'rilaydi (`PaymentMethodCorrection`). */
  correctSaleMethod: (id: number, usul: PaymentMethod | null) =>
    req<Sale>(`/sales/${id}/tolov-usuli`, {
      method: "PATCH",
      body: JSON.stringify({ tolov_usuli: usul }),
    }),
  cancelSale: (id: number) =>
    req<Sale>(`/sales/${id}/cancel`, { method: "POST" }),

  // ----- Jarayondagi sotuvlar (band qilish → tasdiqlash) -----
  // Yo'llar `/mobile/*` ostida, lekin ular mobil ilovaga tegishli emas —
  // jarayonda hayot sikli o'sha routerda yashaydi va ustaxona egasi uchun
  // ochiq (require_role: owner|employee, workshop_id bo'yicha filtrlangan).
  jarayondaSales: () => req<JarayondaSale[]>("/mobile/sales/jarayonda"),
  createJarayonda: (data: {
    items: { product_id: number; quantity: string }[];
    izoh?: string | null;
    branch_id?: number | null;
    client_uuid?: string;
  }) =>
    req<Sale>("/mobile/sales/jarayonda", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  confirmJarayonda: (
    id: number,
    data: {
      sold_items?: { product_id: number; quantity: string }[];
      returned_items?: { product_id: number; quantity: string }[];
      client_id?: number | null;
      debtor_name?: string | null;
      tolov_holati?: PaymentStatus;
      tolangan_summa?: string | null;
    },
  ) =>
    req<Sale>(`/mobile/sales/${id}/tasdiqlash`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  cancelJarayonda: (id: number) =>
    req<Sale>(`/mobile/sales/${id}/bekor`, { method: "PATCH" }),

  // ----- Qaytarishlar (return hujjati) -----
  returns: (params: Record<string, string | number | undefined> = {}) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== "" && v !== null) q.set(k, String(v));
    }
    return req<Page<ReturnDoc>>(`/returns?${q.toString()}`);
  },
  createReturn: (data: {
    return_type: ReturnTypeT;
    original_document_id: number;
    reason?: string | null;
    items: { product_id: number; quantity: string }[];
    tolov_usuli?: PaymentMethod | null;
    client_uuid?: string;
  }) => req<ReturnDoc>("/returns", { method: "POST", body: JSON.stringify(data) }),
  getReturn: (id: number) => req<ReturnDoc>(`/returns/${id}`),

  // ----- Qarz daftar -----
  debtClients: (hammasi = false) =>
    req<DebtClient[]>(`/debts/clients${hammasi ? "?hammasi=true" : ""}`),
  debtClient: (clientId: number) =>
    req<DebtClientHistory>(`/debts/clients/${clientId}`),
  debtLedger: (manba = "all", tugallangan = false) =>
    req<DebtLedgerOut>(
      `/debts/ledger?manba=${encodeURIComponent(manba)}${tugallangan ? "&tugallangan=true" : ""}`,
    ),
  // Mijoz balanslari yig'indisi. `debtLedger.jami_debitorlik` dan FARQ QILADI:
  // u ochiq hujjatlar qoldig'ini, bu esa mijoz balanslarini yig'adi — qo'lda
  // kiritilgan to'lov faqat ikkinchisini harakatlantiradi.
  debtSummary: () => req<DebtSummary>("/debts/summary"),
  debtNamedDebtors: (hammasi = false) =>
    req<DebtNamedDebtor[]>(`/debts/debtors${hammasi ? "?hammasi=true" : ""}`),
  debtNamedDebtorHistory: (nom: string) =>
    req<DebtNamedDebtorHistory>(
      `/debts/debtors/history?nom=${encodeURIComponent(nom)}`,
    ),
  debtTolov: (data: DebtTolovBody) =>
    req<DebtTolovResult>("/debts/tolov", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  debtTolovBekor: (txId: number) =>
    req<DebtTolovResult>(`/debts/tolov/${txId}/bekor`, { method: "POST" }),
  debtReminderSettings: () => req<{ workshop_name: string; template: string; extension_days: number | null; preview: string }>("/debts/reminder-settings"),
  updateDebtReminderSettings: (data: { template: string; extension_days: number | null }) => req<{ workshop_name: string; template: string; extension_days: number | null; preview: string }>("/debts/reminder-settings", { method: "PUT", body: JSON.stringify(data) }),
  sendDebtReminder: (clientId: number, idempotencyKey: string) => req<{ status: string; sent: boolean; message?: string }>(`/debts/clients/${clientId}/reminder/send`, { method: "POST", body: JSON.stringify({ idempotency_key: idempotencyKey }) }),

  // ----- Hisobdan chiqarish (spisaniye) -----
  writeOffs: (params: Record<string, string | number | undefined> = {}) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== "" && v !== null) q.set(k, String(v));
    }
    return req<Page<WriteOff>>(`/write-offs?${q.toString()}`);
  },
  createWriteOff: (data: {
    sabab: WriteOffReason;
    izoh?: string | null;
    branch_id?: number;
    lines: { product_id: number; miqdor: string }[];
    client_uuid?: string;
  }) => req<WriteOff>("/write-offs", { method: "POST", body: JSON.stringify(data) }),
  getWriteOff: (id: number) => req<WriteOff>(`/write-offs/${id}`),

  // ----- Yetkazib beruvchi (supplier) — katalog + kreditorlik hisob-kitobi -----
  // `faol` sukut bo'yicha yuborilmaydi (= hammasi); yangi xarid formasi
  // `true` uzatadi, ya'ni o'chirilgan yetkazib beruvchi taklif qilinmaydi.
  suppliers: (q?: string, faol?: boolean) => {
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    if (faol !== undefined) qs.set("faol", String(faol));
    qs.set("page_size", "200");
    return req<Page<Supplier>>(`/suppliers?${qs.toString()}`);
  },
  createSupplier: (d: {
    name: string;
    phone?: string | null;
    contact_person?: string | null;
    address?: string | null;
    note?: string | null;
  }) => req<Supplier>("/suppliers", { method: "POST", body: JSON.stringify(d) }),
  updateSupplier: (
    id: number,
    d: Partial<{
      name: string;
      phone: string | null;
      contact_person: string | null;
      address: string | null;
      note: string | null;
      faol: boolean;
    }>,
  ) => req<Supplier>(`/suppliers/${id}`, { method: "PATCH", body: JSON.stringify(d) }),
  /** Yumshoq o'chirish — balans nol bo'lmasa 409 `supplier_has_balance`. */
  deactivateSupplier: (id: number) =>
    req<void>(`/suppliers/${id}`, { method: "DELETE" }),
  supplierSummary: () => req<PayableSummary>("/suppliers/summary"),
  payableSuppliers: (hammasi = false) =>
    req<PayableSupplier[]>(
      `/suppliers/payables${hammasi ? "?hammasi=true" : ""}`,
    ),
  supplierHistory: (id: number) => req<SupplierHistory>(`/suppliers/${id}`),
  supplierTolov: (d: {
    supplier_id: number;
    summa: string;
    tolov_usuli?: PaymentMethod | null;
    izoh?: string | null;
    /** Ortiqcha to'lov (avans) uchun majburiy tasdiq — mijoz tomoni bilan bir xil. */
    avans_tasdiqlandi?: boolean;
  }) =>
    req<SupplierTransaction>("/suppliers/tolov", {
      method: "POST",
      body: JSON.stringify(d),
    }),
  supplierQolda: (d: { supplier_id: number; summa: string; izoh: string }) =>
    req<SupplierTransaction>("/suppliers/qolda", {
      method: "POST",
      body: JSON.stringify(d),
    }),

  // ----- Murojaatlar (support tickets) -----
  myTickets: () => req<SupportTicket[]>("/support/tickets/my"),
  workshopTickets: () => req<WorkshopTicket[]>("/support/tickets/workshop"),
  createTicket: (d: { type: TicketType; title: string; body: string }) =>
    req<{ id: number; status: TicketStatus }>("/support/tickets", {
      method: "POST",
      body: JSON.stringify(d),
    }),
};

// Butun summani oddiy bo'sh joy bilan minglikka ajratish (NBSP emas). Kanonik
// versiya lib/format.groupInt — bu yerda import siklidan qochish uchun takror.
function groupSom(n: number): string {
  return String(Math.round(Math.abs(n)))
    .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

export function fmtMoney(s: string | number | null | undefined): string {
  if (s === null || s === undefined) return "0 so'm";
  const n = typeof s === "string" ? parseFloat(s) : s;
  if (Number.isNaN(n)) return "0 so'm";
  return (n < 0 ? "-" : "") + groupSom(n) + " so'm";
}

export interface ProfitLossPeriod {
 from_date: string; to_date: string; status: "complete" | "estimated" | "incomplete";
 reasons: string[]; totals: Record<string, string | null>; categories: Record<string, string>;
 issues: {code: string; date: string; source_type: string | null; source_id: number | null}[];
 sources: {component: string; amount: string | null; date: string; source_type: string; source_id: number; branch_id: number | null; category: string | null}[];
}
export interface ProfitLossReport extends ProfitLossPeriod {
 previous: ProfitLossPeriod; grain: string;
 trend: Pick<ProfitLossPeriod, "from_date" | "to_date" | "status" | "totals">[];
}
